import React, { useEffect } from "react";
import { Helmet } from 'react-helmet';
import { useTranslation } from "react-i18next";



const BusRent = () => {
  const { t } = useTranslation();

  const services = [
    { title: t('busrent_wedding'), detail: t('busrent_wedding_desc') },
    { title: t('busrent_family'), detail: t('busrent_family_desc') },
    { title: t('busrent_picnic'), detail: t('busrent_picnic_desc') },
    { title: t('busrent_corporate'), detail: t('busrent_corporate_desc') },
    { title: t('busrent_airport'), detail: t('busrent_airport_desc') },
    { title: t('busrent_tour'), detail: t('busrent_tour_desc') },
  ];

  const highlights = [
    t('busrent_highlight1'),
    t('busrent_highlight2'),
    t('busrent_highlight3'),
    t('busrent_highlight4'),
  ];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="bg-bg-white">
      <Helmet>
        <title>Bus Rental Services | Khajuraho Routes</title>
        <meta name="description" content="Rent a luxury bus in Khajuraho for weddings, corporate events, family tours, and group travel. Safe, comfortable, and affordable." />
        <meta name="keywords" content="bus rent Khajuraho, luxury bus booking, tempo traveller Khajuraho, group travel MP" />
        <link rel="canonical" href="https://khajurahoroutes.com/busrent" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://khajurahoroutes.com/busrent" />
        <meta property="og:title" content="Bus Rental Services | Khajuraho Routes" />
        <meta property="og:description" content="Rent a luxury bus in Khajuraho for weddings, corporate events, family tours, and group travel. Safe, comfortable, and affordable." />
        <meta property="og:image" content="https://khajurahoroutes.com/android-chrome-512x512.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bus Rental Services | Khajuraho Routes" />
        <meta name="twitter:description" content="Rent a luxury bus in Khajuraho for weddings, corporate events, family tours, and group travel. Safe, comfortable, and affordable." />
        <meta name="twitter:image" content="https://khajurahoroutes.com/android-chrome-512x512.png" />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Khajuraho Routes Bus Rental",
              "provider": {
                "@type": "TravelAgency",
                "name": "Khajuraho Routes"
              },
              "areaServed": {
                "@type": "City",
                "name": "Khajuraho"
              },
              "description": "Luxury bus, tempo traveller, and coach rental services in Khajuraho for groups and events."
            }
          `}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-sm tracking-[0.2em] uppercase text-orange-main font-semibold">
              {t('busrent_title')}
            </p>
            <h1 className="text-4xl md:text-5xl font-semibold leading-tight text-black-custom mt-4">
              {t('busrent_subtitle')}
            </h1>
            <p className="text-lg text-gray-dark mt-5 leading-relaxed">
              {t('busrent_desc')}
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[#3b332d]">
              {highlights.map((item) => (
                <div
                  key={item}
                  className="bg-white rounded-xl px-4 py-3 shadow-sm border border-orange-light/20"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-main to-teal-main rounded-[28px] p-8 text-white shadow-[0_30px_70px_rgba(217,116,42,0.2)]">
            <h2 className="text-2xl font-semibold">{t('busrent_services_heading')}</h2>
            <p className="text-sm text-white/80 mt-2">{t('busrent_services_sub')}</p>
            <div className="mt-6 grid grid-cols-1 gap-4">
              {services.map((service) => (
                <div
                  key={service.title}
                  className="bg-white/10 border border-white/20 rounded-2xl p-4"
                >
                  <h3 className="text-lg font-semibold">{service.title}</h3>
                  <p className="text-sm text-white/80 mt-1">{service.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          <div className="bg-white rounded-[28px] p-8 shadow-[0_24px_60px_rgba(0,0,0,0.12)] border border-orange-light/20">
            <h2 className="text-3xl font-semibold text-black-custom">
              {t('busrent_quick_query')}
            </h2>
            <p className="text-sm text-gray-dark mt-2">
              {t('busrent_query_desc')}
            </p>

            <form className="mt-8 grid grid-cols-1 gap-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input
                  className="w-full rounded-xl border border-[#eadfd4] bg-[#fffaf6] px-4 py-3 text-[#3b332d] outline-none focus:border-[#b34a1d]"
                  placeholder={t('busrent_form_name')}
                  type="text"
                />
                <input
                  className="w-full rounded-xl border border-[#eadfd4] bg-[#fffaf6] px-4 py-3 text-[#3b332d] outline-none focus:border-[#b34a1d]"
                  placeholder={t('busrent_form_phone')}
                  type="tel"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <select className="w-full rounded-xl border border-[#eadfd4] bg-[#fffaf6] px-4 py-3 text-[#3b332d] outline-none focus:border-[#b34a1d]">
                  <option>{t('busrent_form_bus_type')}</option>
                  <option>{t('busrent_bus_mini')}</option>
                  <option>{t('busrent_bus_ac')}</option>
                  <option>{t('busrent_bus_non_ac')}</option>
                  <option>{t('busrent_bus_luxury')}</option>
                </select>
                <input
                  className="w-full rounded-xl border border-[#eadfd4] bg-[#fffaf6] px-4 py-3 text-[#3b332d] outline-none focus:border-[#b34a1d]"
                  placeholder={t('busrent_form_persons')}
                  type="number"
                  min="1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <select className="w-full rounded-xl border border-[#eadfd4] bg-[#fffaf6] px-4 py-3 text-[#3b332d] outline-none focus:border-[#b34a1d]">
                  <option>{t('Function Type')}</option>
                  <option>{t('Wedding')}</option>
                  <option>{t('Family Trip')}</option>
                  <option>{t('Picnic')}</option>
                  <option>{t('Corporate')}</option>
                  <option>{t('Other')}</option>
                </select>
                <input
                  className="w-full rounded-xl border border-[#eadfd4] bg-[#fffaf6] px-4 py-3 text-[#3b332d] outline-none focus:border-[#b34a1d]"
                  type="date"
                />
              </div>

              <textarea
                className="w-full rounded-xl border border-[#eadfd4] bg-[#fffaf6] px-4 py-3 text-[#3b332d] outline-none focus:border-[#b34a1d] resize-none"
                rows="4"
                placeholder={t('Pickup location, destination, and special notes')}
              />

              <button className="bg-orange-main hover:opacity-90 transition text-white px-6 py-3 rounded-full font-semibold w-full md:w-fit">
                {t('Send Query')}
              </button>
            </form>
          </div>

          <div className="bg-gradient-to-br from-orange-light/10 to-teal-light/10 rounded-[28px] p-8 border border-orange-light/20 flex flex-col justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.25em] text-orange-main font-semibold">
                {t('busrent_highlights_heading')}
              </p>
              <h3 className="text-2xl font-semibold text-black-custom mt-4">
                {t('Clean buses, trusted drivers, on-time service')}
              </h3>
              <p className="text-gray-dark mt-3 leading-relaxed">
                {t('We plan routes carefully, share transparent pricing, and provide the right vehicle as per your group size and event type.')}
              </p>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4">
              {highlights.map((item) => (
                <div
                  key={item}
                  className="rounded-xl bg-white/80 border border-white/60 px-4 py-3 text-black-custom shadow-sm"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusRent;
